// PartnerBrandsComponent.js
import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

const marqueeLeft = keyframes`
  0%   { transform: translate3d(0,0,0); }
  100% { transform: translate3d(-33.3333%,0,0); }
`;

const marqueeRight = keyframes`
  0%   { transform: translate3d(-33.3333%,0,0); }
  100% { transform: translate3d(0,0,0); }
`;

const logoBox = {
  width: 281,
  height: 156,
  backgroundColor: '#FFFFFF',
  borderRadius: '48px',
  boxShadow:
    '0px 6px 16px 0px rgba(22, 47, 59, 0.12), 0px 1px 2px 0px rgba(162, 172, 177, 0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flex: '0 0 auto',
};

function PartnerLogoCard({ logo, index, sx, navigate }) {
  const isClickable = Boolean(logo?.url);
  const Component = isClickable ? 'button' : 'div';

  return (
    <Box
      component={Component}
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? () => navigate(logo.url) : undefined}
      aria-label={
        isClickable ? `View ${logo.imageAltTitle || logo.imgAltTag}` : undefined
      }
      sx={{
        ...logoBox,
        ...sx,
        appearance: 'none',
        border: 'none',
        font: 'inherit',
        p: 0,
        pointerEvents: isClickable ? 'auto' : 'none',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': isClickable
          ? {
              transform: 'translateY(-2px)',
              boxShadow:
                '0px 10px 22px 0px rgba(22, 47, 59, 0.16), 0px 2px 6px 0px rgba(162, 172, 177, 0.16)',
            }
          : undefined,
      }}
    >
      {logo && (
        <Image
          src={logo.logo}
          alt={logo.imgAltTag || `partner-${index}`}
          title={logo.imageAltTitle || ''}
          loading="eager"
          width={281}
          height={156}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
}

function TickerRow({ logos, direction = 'left', navigate }) {
  const anim = direction === 'left' ? marqueeLeft : marqueeRight;

  // Pad logos so each copy is at least 7 cards wide (~2135px).
  // Fills without adjacent duplicates and ensures last ≠ first so every seam is clean.
  const copy = React.useMemo(() => {
    const MIN = 7;
    if (logos.length === 0) return logos;
    if (logos.length >= MIN) return logos;
    if (logos.length === 1) {
      const out = [];
      while (out.length < MIN) out.push(logos[0]);
      return out;
    }
    const src = logos;
    const n = src.length;
    const out = [...src];
    let i = 0;
    while (out.length < MIN || out[out.length - 1] === out[0]) {
      const candidate = src[i % n];
      i++;
      if (candidate !== out[out.length - 1]) out.push(candidate);
      if (i > MIN * n * 3) break;
    }
    return out;
  }, [logos]);

  // Scale duration so all rows scroll at the same px/s speed as 6 logos at 30s (12-logo reference)
  const duration = copy.length * 5;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        maxWidth: '100vw',
        mx: 'calc(50% - 50vw)',
        overflow: 'hidden',
        zIndex: 10,
        py: '28px',
        my: '-16px',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          paddingRight: '24px',
          width: 'max-content',
          animation: `${anim} ${duration}s linear infinite`,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        {[...copy, ...copy, ...copy].map((logo, i) => (
          <PartnerLogoCard key={i} logo={logo} index={i} navigate={navigate} />
        ))}
      </Box>
    </Box>
  );
}

const PartnerBrandsComponent = ({ data }) => {
  const navigate = useNavigate();
  const {
    title,
    description,
    footer,
    footerLink,
    footerLinkDescription,
    logos = [],
  } = data;

  // Both rows get all logos, independently shuffled so they look different
  const { top, bottom } = React.useMemo(() => {
    if (!logos || logos.length === 0) {
      return { top: [], bottom: [] };
    }
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    return { top: shuffle(logos), bottom: shuffle(logos) };
  }, [logos]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1010,
          height: 'auto',
          backgroundColor: '#E3E6E8',
          borderRadius: '48px',
          position: 'relative',
          py: '32px',
          boxSizing: 'border-box',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        {/* Title & Description */}
        {(title || description) && (
          <Box sx={{ width: '100%' }}>
            {title && (
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '3rem',
                  lineHeight: '3.25rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  mb: '16px',
                  px: '10px',
                }}
              >
                {title}
              </Typography>
            )}

            {description && (
              <Typography
                component={'p'}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1.5rem',
                  lineHeight: '1.75rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  mb: '40px',
                  px: '32px',
                }}
              >
                {description.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                  /^\*\*[^*]+\*\*$/.test(part) ? (
                    <strong key={i} style={{ fontWeight: 700 }}>
                      {part.slice(2, -2)}
                    </strong>
                  ) : (
                    part
                  )
                )}
              </Typography>
            )}
          </Box>
        )}

        {/* Tickers using the random 12 */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {top.length > 0 && (
            <TickerRow logos={top} direction="right" navigate={navigate} />
          )}
          {bottom.length > 0 && (
            <TickerRow logos={bottom} direction="left" navigate={navigate} />
          )}
        </Box>

        {/* Footer */}
        {(footer || footerLinkDescription) && (
          <Box sx={{ mt: '40px' }}>
            {footer && (
              <Typography
                component={'p'}
                sx={{
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  maxWidth: '600px',
                  m: '0 auto',
                  mb: '25px',
                  whiteSpace: 'pre-line',
                  px: '32px',
                }}
              >
                {footer}
              </Typography>
            )}
            {footerLinkDescription && (
              <LinkWithIconComponent
                href={footerLink || undefined}
                iconSize="1rem"
                sx={{
                  mb: '15px',
                  textAlign: 'center',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                }}
              >
                {footerLinkDescription}
              </LinkWithIconComponent>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PartnerBrandsComponent;
